import axios, { type AxiosInstance } from "axios";
import { ignitionApiUrl } from "@/core/services/protocols";
import RawAxiosTokenInterceptor from "@/core/services/middleware/RawAxiosTokenInterceptor";
import type { IHttpTokenInterceptor } from "@/core/services/middleware/HttpTokenInterceptor";
import type {
  HttpClient,
  IHttpDeleteParams,
  IHttpGetParams,
  IHttpPatchParams,
  IHttpPostParams,
  IHttpPutParams,
  IHttpResponse
} from "@/core/services/protocols/types";

function getIgnitionBaseUrl() {
  return ignitionApiUrl || import.meta.env.VITE_APP_IGNITION_URL || "";
}

export class IgnitionHttpClient implements HttpClient {
  private readonly axios: AxiosInstance;

  constructor(
    private readonly baseURL?: string,
    axiosTokenInterceptor: IHttpTokenInterceptor<AxiosInstance> = new RawAxiosTokenInterceptor()
  ) {
    this.axios = axios.create();

    // Axios executa os interceptores de request na ordem inversa do registro.
    axiosTokenInterceptor.intercept(this.axios);

    this.axios.interceptors.request.use((config) => {
      config.baseURL = this.baseURL ?? getIgnitionBaseUrl();

      return config;
    });
  }

  async get<T>(params: IHttpGetParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.get(params.url, {
      params: params.queryParams,
      ...params.config
    });

    return {
      statusCode: request.status,
      body: request.data,
      headers: request.headers
    };
  }

  async post<T>(params: IHttpPostParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.post(
      params.url,
      params.body,
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data,
      headers: request.headers
    };
  }

  async put<T>(params: IHttpPutParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.put(params.url, params.body);

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async patch<T>(params: IHttpPatchParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.patch(
      params.url,
      params.body,
      params.config
    );

    return {
      statusCode: request.status,
      body: request.data
    };
  }

  async delete<T>(params: IHttpDeleteParams): Promise<IHttpResponse<T>> {
    const request = await this.axios.delete(params.url, {
      data: params.body,
      params: params.params
    });

    return {
      statusCode: request.status,
      body: request.data
    };
  }
}

export const ignitionHttpClient = new IgnitionHttpClient();
